import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { describeError } from '../../components/ErrorToast';
import { StepCard } from './VendorReportStep';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = 'w-full border border-slate-300 rounded px-2 py-1 text-[11px] bg-white';
const lbl = 'block text-[10px] uppercase font-bold text-slate-600 mb-0.5';

function ReassignForm({ ticket, onDone, onCancel }) {
  const [d, setD] = useState(null);
  const [mode, setMode] = useState('stock');
  const [f, setF] = useState({ new_serial_id: '', new_serial_no: '', warranty_end_date: '', reason: 'Beyond repair' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ha/service-tickets/${ticket.ticket_no}/reassign-defaults`).then(({ data }) => {
      setD(data);
      setF((x) => ({ ...x, warranty_end_date: data.warranty_end_date || '' }));
      if (!data.stock.length) setMode('new');
    }).catch((e) => setErr(describeError(e, 'Could not load stock')));
  }, [ticket.ticket_no]);

  const submit = async () => {
    if (!window.confirm('Assign the new serial? The old serial will be retired and kept as "replaced serial".')) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/ha/service-tickets/${ticket.ticket_no}/reassign-serial`, {
        mode, reason: f.reason, warranty_end_date: f.warranty_end_date || undefined,
        ...(mode === 'stock' ? { new_serial_id: f.new_serial_id } : { new_serial_no: f.new_serial_no.trim() }),
      });
      onDone();
    } catch (e) { setErr(describeError(e, 'Could not reassign serial')); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="serial-reassign-form">
      <div className="text-[11px] text-slate-600">Current serial: <b className="font-mono" data-testid="serial-reassign-old">{d?.old_serial_no || ticket.serial_no || '—'}</b></div>
      <div className="flex gap-2">
        {[['stock', 'Pick from stock'], ['new', 'Type vendor replacement serial']].map(([k, label]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`serial-reassign-mode-${k}`}
            className={`flex-1 px-2 py-1 rounded border text-[11px] font-semibold ${mode === k ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-300 hover:bg-slate-50'}`}>{label}</button>
        ))}
      </div>
      {mode === 'stock' ? (
        <div><label className={lbl}>Unit in stock *</label>
          <select value={f.new_serial_id} onChange={(e) => setF({ ...f, new_serial_id: e.target.value })} data-testid="serial-reassign-stock" className={inp}>
            <option value="">{d?.stock?.length ? 'Select a serial…' : 'No unsold units in stock'}</option>
            {(d?.stock || []).map((s) => <option key={s.serial_id} value={s.serial_id}>{s.serial_no}</option>)}
          </select></div>
      ) : (
        <div><label className={lbl}>New serial number *</label>
          <input value={f.new_serial_no} onChange={(e) => setF({ ...f, new_serial_no: e.target.value })} placeholder="Serial on the replacement unit" data-testid="serial-reassign-new-no" className={inp} />
          <div className="text-[10px] text-slate-500 mt-0.5">A new inventory record is created for this serial.</div></div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div><label className={lbl}>Warranty end date</label><input type="date" value={f.warranty_end_date} onChange={(e) => setF({ ...f, warranty_end_date: e.target.value })} data-testid="serial-reassign-warranty" className={inp} /></div>
        <div><label className={lbl}>Reason</label><input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="serial-reassign-reason" className={inp} /></div>
      </div>
      {err && <div className="text-[11px] text-rose-700" data-testid="serial-reassign-error">{err}</div>}
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} data-testid="serial-reassign-cancel" className="px-3 py-1 rounded border border-slate-300 text-[11px]">Cancel</button>
        <button onClick={submit} disabled={busy || (mode === 'stock' ? !f.new_serial_id : !f.new_serial_no.trim())} data-testid="serial-reassign-submit"
          className="px-3 py-1 rounded bg-rose-600 text-white text-[11px] font-bold disabled:opacity-50">{busy ? 'Saving…' : 'Assign new serial'}</button>
      </div>
    </div>
  );
}

export default function SerialReassignStep({ ticket, canReassign, onChanged }) {
  const [open, setOpen] = useState(false);
  const done = ticket.replaced_serial_no || ticket.replaced_serial_id;
  return (
    <StepCard n="D" title="Serial reassignment (beyond repair)" testid="step-serial-reassign">
      {done ? (
        <div className="space-y-0.5" data-testid="serial-reassign-done">
          <div>Active serial: <b className="font-mono" data-testid="serial-reassign-active">{ticket.serial_no}</b></div>
          <div className="text-slate-600">Replaced serial: <span className="font-mono line-through" data-testid="serial-reassign-replaced">{ticket.replaced_serial_no}</span></div>
          <div className="text-[10.5px] text-slate-500">{ticket.serial_reassign_reason} · by {ticket.serial_reassigned_by_name || '—'} · {ticket.serial_reassigned_at && new Date(ticket.serial_reassigned_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
        </div>
      ) : open ? (
        <ReassignForm ticket={ticket} onCancel={() => setOpen(false)} onDone={() => { setOpen(false); onChanged(); }} />
      ) : canReassign ? (
        <button onClick={() => setOpen(true)} data-testid="serial-reassign-open" className="text-[11px] font-semibold text-rose-700 hover:underline">Device beyond repair — assign a new serial →</button>
      ) : (
        <div className="italic text-slate-400" data-testid="serial-reassign-no-access">Only Owner, Admin, Front Office or Audiologist can reassign serials.</div>
      )}
    </StepCard>
  );
}
